import { type Constructor } from '../types';
import { type fabric } from 'fabric';
import { hasEmbeddedTextBehaviors, isTable } from './guards';

export type StyleDefinition = Record<string, unknown>;

interface StyleCopyPasteBehaviors {
  __hasStyleCopyPasteBehaviors: boolean;

  allAllowedStyleCopyPasteFields(): string[];

  filterToAllowedFields(styles: StyleDefinition): StyleDefinition;

  applyPaste(styles: StyleDefinition): void;

  getCopyStyles(): StyleDefinition;

  allowedStyleCopyPasteFields(): string[];

  canCopyStyles(): boolean;

  canPasteStyles(): boolean;
}

/**
 * Type of an decorated fabric object that implements the copy paste interface
 */
export type StyleCopyPastable = fabric.Object & StyleCopyPasteBehaviors;

// eslint-disable-next-line @typescript-eslint/no-redeclare
function StyleCopyPasteBehaviors<T extends Constructor<fabric.Object>>(
  Base: T
) {
  return class WithStyleCopyPasteBehaviors
    extends Base
    implements StyleCopyPasteBehaviors
  {
    __hasStyleCopyPasteBehaviors: boolean = true;

    allowedStyleCopyPasteFields(): string[] {
      return [];
    }

    allAllowedStyleCopyPasteFields(): string[] {
      const fields = this.allowedStyleCopyPasteFields();

      if (
        hasEmbeddedTextBehaviors(this) &&
        (this.hasEmbeddedText() || this.hasTextFields)
      ) {
        // ['bold', 'italic', 'code', 'strikethrough', 'underline']
        fields.push(
          'isTextBold',
          'isTextItalic',
          'isTextCode',
          'isTextStrikethrough',
          'isTextUnderline',
          'currentTextColor'
        );
        if (this.textProps.fontSize === 'user') {
          fields.push('fontSize');
        }
        if (this.textProps.textAlign === 'user') {
          fields.push('textAlign');
        }
        if (this.textProps.verticalAlign === 'user') {
          fields.push('verticalAlign');
        }
        fields.push('textListType');
        fields.push('textOpacity');
      }

      return fields;
    }

    canCopyStyles() {
      return this.allAllowedStyleCopyPasteFields().length > 0;
    }

    canPasteStyles() {
      return this.allAllowedStyleCopyPasteFields().length > 0;
    }

    getCopyStyles(): StyleDefinition {
      return this.allAllowedStyleCopyPasteFields().reduce(
        (out: StyleDefinition, k: string) => {
          const value = this.getStyleFieldValue(k);
          // Fields must have an explicit value
          if (value !== null && value !== undefined) {
            out[k] = value;
          }
          return out;
        },
        {}
      );
    }

    getStyleFieldValue(field: string, obj: fabric.Object = this) {
      if (field === 'fontSize') {
        return obj.fontSizeWithDefault();
      }

      if (isTable(obj) && obj.hasSelectedCell()) {
        const value = obj.getCellStyle(field);
        if (value !== undefined) {
          return value;
        }
      }
      return obj[field];
    }

    filterToAllowedFields(styles: StyleDefinition): StyleDefinition {
      return this.allAllowedStyleCopyPasteFields().reduce(
        (filtered: StyleDefinition, field: string) => {
          if (field in styles) {
            filtered[field] = styles[field];
          }
          return filtered;
        },
        {}
      );
    }

    applyPaste(styles: StyleDefinition): void {
      const controlled = this.getControlledProperties() ?? {};
      const allowed = Object.fromEntries(
        Object.entries(this.filterToAllowedFields(styles)).filter(
          ([k]) => !(k in controlled)
        )
      );

      this.setOnObjects(allowed);
    }
  };
}

export default StyleCopyPasteBehaviors;
